import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { twMerge } from "tailwind-merge";

import { Button, CancelSubmitButtons, errorToast } from "~/common/components";
import Modal from "~/common/components/Modal";
import { FileUpload } from "~/common/components/ui/FileUpload";
import Input from "~/common/components/ui/Input";
import { fileSizeToBytes } from "~/common/constants";
import { consentDocumentsQueryOptionsFactory } from "~/providers/factories/consentDocumentsQueryOptionsFactory";
import { useCurrentTeamMemberContext } from "~/providers/hooks";
import { useAddConsentDocument } from "~/providers/hooks/useAddConsentDocument";
import { addDocumentFormSchema } from "~/providers/schemas/consentDocuments";
import { AddDocumentForm } from "~/providers/types/consentDocuments";

export const AddDocumentModal = () => {
  const [show, setShow] = useState(false);
  const currentTeamMember = useCurrentTeamMemberContext();
  const addConsentDocumentMutation = useAddConsentDocument();
  const queryClient = useQueryClient();
  const {
    handleSubmit,
    control,
    register,
    formState: { errors, isValid },
    watch,
    reset,
  } = useForm<AddDocumentForm>({
    resolver: zodResolver(addDocumentFormSchema),
    mode: "onChange",
  });

  const organizationId =
    currentTeamMember.organization_team_members[0].organization_id;

  const onCloseModal = () => {
    if (addConsentDocumentMutation.isPending) return;
    reset();
    setShow(false);
  };

  const onSubmit: SubmitHandler<AddDocumentForm> = ({ name, file }) => {
    addConsentDocumentMutation.mutate(
      {
        name,
        file,
        organizationId,
      },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({
            queryKey: consentDocumentsQueryOptionsFactory.list({
              organizationId,
            }).queryKey,
          });
          onCloseModal();
          toast.success("Consent added successfully");
        },
        onError: (error) => {
          errorToast(error);
        },
      },
    );
  };

  return (
    <div>
      <Button
        onClick={() => {
          setShow(true);
        }}
        className="ml-auto w-fit whitespace-nowrap"
      >
        Add document
      </Button>
      <Modal
        title="Add document"
        show={show}
        onClose={() => onCloseModal()}
        className="xl:w-1/2"
      >
        <form
          noValidate
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col gap-6"
        >
          <Input
            id="name"
            label="Name (patient facing)"
            required
            maxLength={100}
            charactersCount={watch("name")?.length}
            placeholder="e.g. Clinic terms and policies"
            error={errors.name?.message}
            {...register("name")}
          />

          <Controller
            control={control}
            name="file"
            render={({ field }) => {
              return (
                <FileUpload.Root
                  accept={{ "application/pdf": [".pdf"] }}
                  maxSize={fileSizeToBytes.fromMB(10)}
                  onFilesChange={(files) => {
                    field.onChange(files[0]);
                  }}
                  error={errors.file?.message}
                >
                  <FileUpload.Dropzone
                    className={twMerge(
                      errors.file && "border-red-400 bg-red-50",
                    )}
                  >
                    <FileUpload.Content />
                  </FileUpload.Dropzone>
                  <FileUpload.List />
                </FileUpload.Root>
              );
            }}
          />

          <CancelSubmitButtons
            submitButtonLabel="Add document"
            handleCancel={() => onCloseModal()}
            isSubmitting={addConsentDocumentMutation.isPending}
            containerClassName="w-fit ml-auto"
            disableSubmit={!isValid || addConsentDocumentMutation.isPending}
            disableCancel={addConsentDocumentMutation.isPending}
          />
        </form>
      </Modal>
    </div>
  );
};
